import { isBool, isFunc, isNumber, isObject } from './utils/js';
import { getDocumentLang } from './utils/lang';

// Match with bin/lang.js
const DEFAULT_CONTEXT = '_';

export class I18n {
  _locale = getDocumentLang('en');

  _defaultLocale = 'en';

  _langData = {};

  _warnedLocales = new Set();

  _warnMissingLocaleOnce(locale) {
    if (
      locale !== this._defaultLocale &&
      !this._warnedLocales.has(locale) &&
      !this._langData[locale]
    ) {
      this._warnedLocales.add(locale);
      this._log(`No messages registered for locale '${locale}'`);
    }
  }

  _validateLocale(locale) {
    if (![2, 3].includes(locale.length)) {
      throw new Error(
        `Expected a 2–3 letter ISO 639-1/639-2 code, got '${locale}'`,
      );
    }
  }

  _validateMessage(msg) {
    const type = typeof msg;
    if (type !== 'string') {
      throw new TypeError(`Unexpected message format '${type}'`);
    }
  }

  // Separate to allow overriding in tests
  _log(err) {
    // eslint-disable-next-line no-console
    console.warn(err);
  }

  /**
   * Add data for a locale.
   *
   * @param {string} locale - ISO 639-1 or 639-2 language code
   * @param {object} langData - Data for the locale.
   * @param {function(number): number} langData.getN - Takes a count and
   *   returns a plural index.
   * @param {object} langData.messages - Keys are the source language, values
   *   the translations. The latter will be an array of strings for plurals,
   *   otherwise a single string.
   */
  addLocale(locale, langData) {
    this._validateLocale(locale);
    if (
      !isObject(langData) ||
      !isFunc(langData.getN) ||
      !isObject(langData.messages)
    ) {
      throw new TypeError(
        'Expected a language data object with a `getN` method and a `messages` object',
      );
    }
    const getNVal = langData.getN(1);
    // Some simple plural formulas like `n != 1` result in booleans that
    // will be cast to 0 or 1.
    if (!isNumber(getNVal) && !isBool(getNVal)) {
      throw new TypeError(
        'The getN function must return an index or a boolean',
      );
    }

    // Handle existing messages for the same locale.
    this._langData[locale] ??= {};
    this._langData[locale].getN = langData.getN;
    this._langData[locale].messages ??= {};
    Object.entries(langData.messages).forEach(([context, messages]) => {
      this._langData[locale].messages[context] ??= {};
      Object.assign(this._langData[locale].messages[context], messages);
    });
  }

  /**
   * Set the active locale.
   *
   * @param {string} locale - ISO 639-1 or 639-2 language code
   */
  setLocale(locale) {
    this._validateLocale(locale);
    this._warnMissingLocaleOnce(locale);
    this._locale = locale;
  }

  /**
   * Set the default locale used in the source code.
   *
   * @param {string} locale - ISO 639-1 or 639-2 language code
   */
  setDefaultLocale(locale) {
    this._validateLocale(locale);
    this._defaultLocale = locale;
  }

  /**
   * Get data for the current locale.
   *
   * @returns {object|undefined}
   */
  _getLangData() {
    this._warnMissingLocaleOnce(this._locale);
    return this._langData[this._locale];
  }

  /**
   * Get messages for the current locale, optionally for a context.
   *
   * @param {string} [context]
   * @returns {object}
   */
  _getMessages(context) {
    const langData = this._getLangData();
    return langData ? langData.messages[context || DEFAULT_CONTEXT] || {} : {};
  }

  /**
   * Get a plural message index for the specified item count for the current
   * locale.
   *
   * @param {number} count
   * @returns {number}
   */
  _getPluralIndex(count) {
    const langData = this._getLangData();
    // Fall back to English plural rule, n != 1
    return Number(langData ? langData.getN(count) : count !== 1);
  }

  /**
   * Get a translation of `text`.
   *
   * @param {string} text - Message to translate.
   * @returns {string}
   * @example
   *
   * i18n.t('Mark the date');
   */
  t(text) {
    return this.p(text, '');
  }

  /**
   * Get a translation of `text` with context information for translators
   * (`p` for particular). May result in a different translation than for for
   * the same text without context.
   *
   * https://www.gnu.org/software/gettext/manual/html_node/Contexts.html
   *
   * @param {string} text - Message to translate.
   * @param {string} context - Context information for translators.
   * @returns {string}
   * @example
   *
   * // 'Open' may be translated differently for a calendar compared to other
   * // things like doors, windows or stores, like in Japanese:
   * // https://www.tofugu.com/japanese/akeru-aku-hirakeru-hiraku/
   * i18n.p('Open', 'calendar');
   */
  p(text, context) {
    const texts = this._getMessages(context);
    const msg = texts[text];
    if (!msg) {
      return text;
    }
    this._validateMessage(msg);
    return msg;
  }

  /**
   * Translate the singular or plural form based on the supplied number
   * (`n` for N items).
   *
   * https://www.gnu.org/software/gettext/manual/html_node/Plural-forms.html
   *
   * @param {string} singular - Singular message ID.
   * @param {string} plural - Plural message ID.
   * @param {number} count - The number (e.g. item count) to determine the
   *   translation.
   * @returns {string}
   * @example
   *
   * i18n.n('{{num}} month', '{{num}} months', count).replace('{{num}}', count);
   * // count === 1
   * // => '1 månad'
   * // count === 3
   * // => '3 månader'
   */
  n(singular, plural, count) {
    return this.np(singular, plural, '', count);
  }

  /**
   * Translate the singular or plural form based on the supplied number,
   * with context information for translators. May result in a different
   * translation than for for the same text without context.
   *
   * https://www.gnu.org/software/gettext/manual/html_node/Contexts.html
   * https://www.gnu.org/software/gettext/manual/html_node/Plural-forms.html
   *
   * @param {string} singular - Singular message ID.
   * @param {string} plural - Plural message ID.
   * @param {string} context - Context information for translators.
   * @param {number} count - The number (e.g. item count) to determine the
   *   translation.
   * @returns {string}
   * @example
   *
   * // The calendar context helps differentiate this from online dating,
   * // e.g. 'datum' vs. 'dejt' for Swedish.
   * i18n
   *   .np('{{num}} date', '{{num}} dates', 'calendar', count)
   *   .replace('{{num}}', count);
   */
  np(singular, plural, context, count) {
    const texts = this._getMessages(context);
    const i = this._getPluralIndex(count);
    const options = texts[singular];
    if (!options || !options[i]) {
      return [singular, plural][i];
    }
    const msg = options[i];
    this._validateMessage(msg);
    return msg;
  }
}

// Use a named export to force the `i18n.[method]` naming that's required for
// extracting strings.
export const i18n = new I18n();
